import { useEffect, useRef } from 'react';

/**
 * Close a popover/dropdown when the user clicks (or focuses) outside it, or
 * presses Escape. Attach the returned ref to the wrapper that contains BOTH
 * the trigger and the menu — clicks on the trigger itself count as "inside"
 * so the trigger's own toggle handles open/close without double-firing.
 *
 * Pass `active` so the document listener is only bound while the menu is open.
 */
export function useOutsideClick<T extends HTMLElement = HTMLDivElement>(
  active: boolean,
  onClose: () => void,
) {
  const ref = useRef<T>(null);
  const cb = useRef(onClose);
  cb.current = onClose;

  useEffect(() => {
    if (!active) return;
    function onPointer(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) cb.current();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') cb.current();
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [active]);

  return ref;
}
